import React, { useEffect, useState } from "react";
import {
  Grid, Paper, Typography, Box, styled, ThemeProvider, createTheme, Divider,
  List,
  ListItemText,
  ListItemButton,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { ReactComponent as TotalIcon } from './icons/total.svg';
import { ReactComponent as ActiveIcon } from './icons/active.svg';
import { ReactComponent as InActiveIcon } from './icons/inactive.svg';
import { ReactComponent as AssignNoticeIcon } from './icons/assign_icon.svg';
import { ReactComponent as CancelledNoticeIcon } from './icons/cancelled_icon.svg';
import { ReactComponent as RescheduledNoticeIcon } from './icons/reschedule_icon.svg';
import totalUndeliveredIcon from './icons/total_undelivered.png';
import totalDeliveredIcon from './icons/total_delivered.png';
import totalPartialIcon from './icons/total_partial_delivery.png';
import totalReturnedIcon from './icons/total_returned.png';
import totalRescheduledIcon from './icons/total_reschedule.png';
import totalCancelledIcon from './icons/total_cancelled.png';
import totalPunchedIcon from './icons/punch_orders.png';
import pickUpIcon from './icons/pickup.png';
import dropOffIcon from './icons/dropoff.png';
import PicknDropIcon from './icons/pickndrop.png';
import WithdrawnIcon from './icons/total_withdrawn.png';
import AppWebsiteVisits from "./Components/AppWebsiteVisits";
import SecurityApproval from "./Components/SecurityApproval";
import PndRestApi from "src/utils/pnd/PndRestApi";
import RiderPieChart from "./Components/RiderPieChart";
import EmiratesOrderTable from './Components/EmiratesOrderTable';
import TimeSlotOrderSummary from './Components/TimeSlotOrderSummary';
import LiveMapGrid from './Components/LiveMapGrid';

import { CLIENT_ADMIN, HUB_MANAGER_ROLE_ID, HUB_OPERATOR_ROLE_ID, ITC_ADMIN_ROLE_ID, MERCHANT_ROLE_ID, SECURITY_ROLE_ID, DASHBOARD_REFRESH_INTERVAL } from "src/utils/pnd/PndConstants";
import { Redirect } from 'react-router-dom';
import { useSelector } from "react-redux";


const getColorByIndex = (index) => {
  if (index === 0) return 'gray';      // Total
  if (index === 1) return '#2ab36c';      // Active
  if (index === 2) return '#eb6056';        // Inactive
  return 'gray';
};

const HeadingHubName = ({ hubCode }) => (
  <>
    {hubCode ? hubCode : ""}
  </>
);

const HeaderRow = ({ labels }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1} ml="100px">
    {labels.map((label, index) => (
      <Box
        key={index}
        sx={{
          minWidth: '50px',
          fontWeight: 'bold',
          textAlign: 'center',
          color: getColorByIndex(index),
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

const InfoRow = ({ title, infoList }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    <Box minWidth="100px" fontWeight="bold">
      {title}
    </Box>
    <Box display="flex" gap={1}>
      {infoList?.map((item, index) => (
        <Button
          key={index}
          sx={{
            backgroundColor: getColorByIndex(index),
            color: 'white',
            minWidth: '50px',
            fontWeight: 'bold'
          }}
        >
          {item.value}
        </Button>
      ))}
    </Box>
  </Box>
);

const CategorySection = ({ title, data = [], labels }) => (
  <Box mb={2}>
    {/* Render Category Title */}
    <Typography variant="subtitle1" fontSize="12px" fontWeight="bold" mb={1}>
      {title}
    </Typography>


    {/* Flex container for the row of items */}
    <Box display="flex" gap={1} flexDirection="row" alignItems="center" flexWrap="nowrap">
      {data.map((item, index) => {
        // Set background color based on the index

        return (

          <Box display="flex" gap={1}>

            <Button
              key={index}
              sx={{
                backgroundColor: getColorByIndex(index),
                color: 'white',
                minWidth: '50px',
                fontWeight: 'bold'
              }}
            >
              {item.value}
            </Button>

          </Box>

        );
      })}
    </Box>
  </Box>
);

const CustomPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  boxShadow: 'none',
  backgroundColor: 'transparent',
}));

const CategoryTitle = ({ title }) => (
  <>
    <Typography sx={{ fontSize: 20, marginTop: 1 }} variant="h6">{title}</Typography>
    <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
  </>
);

const AnimatedValue = ({ targetValue }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const numericValue = typeof targetValue === 'string'
      ? parseFloat(targetValue.replace(/[^\d.-]/g, ''))
      : targetValue;

    if (isNaN(numericValue)) {
      setValue(0);
      return;
    }

    let startValue = 0;
    const duration = 2000;
    const increment = numericValue / (duration / 16.67);

    const interval = setInterval(() => {
      startValue += increment;
      if (startValue >= numericValue) {
        startValue = numericValue;
        clearInterval(interval);
      }
      setValue(Math.floor(startValue));
    }, 16.67);

    return () => clearInterval(interval);
  }, [targetValue]);

  return <Typography sx={{ fontSize: 22, color: '#000' }} variant="h6">{value}</Typography>;
};

const getIcon = (label, isHubOperator) => {
  switch (label) {
    case "Assign":
      return AssignNoticeIcon;
    case "Reschedule":
      return RescheduledNoticeIcon;
    case "Cancel":
      return isHubOperator ? null : CancelledNoticeIcon;
    default:
      return null;
  }
};


const mapMerchantWeeklyData = (data, pickup) => {
  return [
    {
      name: `PickUp: ${pickup} Orders`,
      type: "column",
      fill: "gradient",
      data: data.map(day => day.pickup || 0)
    },

  ];
};

const mapMerchantMonthlyData = (data, pickup) => {
  return [
    {
      name: `PickUp: ${pickup} Orders`,
      type: "column",
      fill: "gradient",
      data: data.map(month => month.pickup || 0)
    },

  ];
};


const mapWeeklyData = (data, total, pickup, dropoff, pickanddrop) => {

  return [
    {
      name: `Total: ${total} Orders`,
      type: "column",
      fill: "solid",
      data: data.map(day => day.total || 0)
    },
    {
      name: `PickUp: ${pickup} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(day => day.pickup || 0)
    },
    {
      name: `DropOff: ${dropoff} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(day => day.dropOff || 0)
    },
    {
      name: `Pick&Drop: ${pickanddrop} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(day => day.pickAndDrop || 0)
    }
  ];
};

const mapMonthlyData = (data, total, pickup, dropoff, pickanddrop) => {

  return [
    {
      name: `Total: ${total} Orders`,
      type: "bar",
      fill: "solid",
      data: data.map(month => month.total || 0)
    },
    {
      name: `PickUp: ${pickup} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(month => month.pickup || 0)
    },
    {
      name: `DropOff: ${dropoff} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(month => month.dropOff || 0)
    },
    {
      name: `Pick&Drop: ${pickanddrop} Orders`,
      type: "area",
      fill: "gradient",
      data: data.map(month => month.pickAndDrop || 0)
    }
  ];
};

const ListItem = ({ index, value, title, icon: Icon }) => {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        opacity: animate ? 1 : 0,
        transform: animate ? "scale(1)" : "scale(0.9)",
        transition: "opacity 500ms, transform 500ms"
      }}
      mt={1.3}

    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="white"
        mr={2}
      >
        <Icon style={{ width: 15, height: 15 }} />
      </Box>
      <Typography variant="subtitle1" style={{ flex: 1, fontSize: 19 }}>
        {title}
      </Typography>
      <Typography fontSize={25} variant="h1">{value}</Typography>
    </Box>
  );
};

const AccountSummaryItem = ({ index, value, title, icon: Icon }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        opacity: 1,
        transform: "scale(1)",
        transition: "opacity 500ms, transform 500ms"
      }}
      mt={1.3}

    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="white"
        mr={2}
      >
        <Icon style={{ width: 15, height: 15 }} />
      </Box>
      <Typography variant="subtitle1" style={{ flex: 1, fontSize: 15, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Typography fontSize={15} variant="h1">{value}</Typography>
    </Box>
  );
};

const MerchantItem = ({ index, value, title, icon: iconUrl }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        opacity: animate ? 1 : 0,
        transform: animate ? "scale(1)" : "scale(0.9)",
        transition: "opacity 500ms, transform 500ms"
      }}
      mt={1.5}

    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="white"
        mr={2}
      >
        <img
          src={iconUrl}
          alt={title}
          style={{ width: 35, height: 35 }}
        />        </Box>
      <Typography variant="subtitle1" style={{ flex: 1, fontSize: 19 }}>
        {title}
      </Typography>
      <Typography fontSize={25} variant="h1">{value}</Typography>
    </Box>
  );
};

const NoticeBoard = ({ items = [], onNoticeClick }) => {
  return (
    <Paper variant="outlined" elevation={1} sx={{ justifyContent: "center", marginTop: 2 }}>
      <List
        sx={{ width: '100%', maxHeight: 200, overflowY: 'auto', bgcolor: 'background.paper' }}
        component="nav"
        aria-labelledby="nested-list-subheader"
      >
        {(items || []).map((item, index) => (
          <React.Fragment key={index}>
            <ListItemButton onClick={() => onNoticeClick(item)}>
              <Box display="flex" alignItems="center" width="100%">
                {item.icon && <item.icon style={{ width: 30, height: 30, marginRight: 15 }} />}
                <ListItemText primary={item.title?.toUpperCase()} sx={{ flex: 1, textAlign: "left", fontSize: 12 }} />
                <Typography variant="h6" sx={{ ml: 2, fontSize: 14 }}>{item.value}</Typography>
              </Box>
            </ListItemButton>
            {index < items.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

const CustomCard = ({ title, type, riderInfo, noticeBoardInfo, onNoticeClick }) => (
  <Card variant="outlined" sx={{ width: '100%', height: '100%', borderRadius: 2, borderWidth: 1, backgroundColor: "#fff" }}>
    <CardContent sx={{ padding: 3 }}>
      <Typography sx={{ fontSize: 20, textAlign: "left", color: "#7B7B7B" }} variant="subtitle1">{title}</Typography>
      {
        type === "Rider" && riderInfo != null ?
          <RiderPieChart riderInfoDetails={riderInfo} />
          :
          <NoticeBoard items={noticeBoardInfo} onNoticeClick={onNoticeClick} />
      }
    </CardContent>
  </Card>
);

const MerchantNoticeBoard = ({ title, noticeBoardInfo, onNoticeClick }) => (
  <Card variant="outlined" sx={{ width: '100%', borderRadius: 2, borderWidth: 1, backgroundColor: "#fff" }}>
    <CardContent sx={{ padding: 3 }}>
      <Typography sx={{ fontSize: 20, textAlign: "left", color: "#7B7B7B" }} variant="subtitle1">{title}</Typography>
      <NoticeBoard items={noticeBoardInfo} onNoticeClick={onNoticeClick} />
    </CardContent>
  </Card>
);

const OrderCards = ({ orderInfo }) => {
  return (
    <Grid mt={0.8} container spacing={1}>
      {orderInfo.map((item, index) => (
        <Grid item xs={6} sm={6} md={4} lg={3} xl={2.4} key={index}>
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: 1,
              height: '100%',
              borderRadius: 2,
              borderWidth: 1,
              backgroundColor: '#fff',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <img
                  src={item.icon}
                  alt={item.title}
                  style={{ width: 24, height: 24, marginRight: 8, marginLeft: 4 }}
                />
                <Typography sx={{ fontSize: 11, color: '#000000', fontWeight: 'bold' }} variant="subtitle2">
                  {item.title}
                </Typography>
              </Box>
              <AnimatedValue targetValue={item.value} />
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const ClientSummaryBarComp = ({ clientSummaryStats, hubCode }) => {
  const ROW1 = [
    { label: 'Total Orders', value: clientSummaryStats.totalOrdersToday, color: '#000', suffix: '' },
    { label: 'OTD (%) ', value: clientSummaryStats.otdToday, color: '#FF8C00' },
    { label: 'Van Fill (%)', value: clientSummaryStats.vansFiling, color: '#FF8C00' },
    { label: 'Delivered Orders ', value: clientSummaryStats.ordersDelivered, color: '#32CA73', suffix: '' },
  ];

  const ROW2 = [
    { label: 'Orders Not Assigned', value: clientSummaryStats.ordersNotAssigned, color: '#e53935', suffix: '' },
    { label: 'OTR', value: clientSummaryStats.ordersOnRoad, color: '#000', suffix: '' },
    { label: 'Cancelled Orders', value: clientSummaryStats.ordersCancelled, color: '#e53935', suffix: '' },
    { label: 'Undelivered Orders', value: clientSummaryStats.todayUndeliveredOrders, color: '#FF8C00', suffix: '' },
  ];


  const ROW3 = [
    { label: 'On Duty', value: clientSummaryStats.onDuty, color: '#32CA73', suffix: '' },
    { label: 'Off Duty', value: clientSummaryStats.offDuty, color: '#e53935', suffix: '' },
    { label: 'On Vacation', value: clientSummaryStats.onVacation, color: '#f59e0b', suffix: '' },
    { label: 'On Break', value: clientSummaryStats.onBreak, color: '#64748b', suffix: '' },
    { label: 'Absent', value: clientSummaryStats?.absent, color: '#3b82f6' },
  ];
  const ROW4 = [
    { label: 'On Delivery', value: clientSummaryStats.deliveryBusy, color: '#ef4444', suffix: '' },
    { label: 'Busy At Hub', value: clientSummaryStats.busyOnHub, color: '#8b5cf6', suffix: '' },
    { label: 'Free At Hub', value: clientSummaryStats.freeAtHub, color: '#32CA73', suffix: '' },
    { label: 'Delivered (Free)', value: clientSummaryStats.deliveredFreed, color: '#10b981', suffix: '' },
    { label: 'Available (%)', value: clientSummaryStats.availableRiders, color: '#32CA73' },
  ];


  const cellStyle = {
    flex: 1,
    padding: '4px 12px',
    borderRight: '1px solid #e0e0e0',
    minWidth: 0,
  };
  const lastCellStyle = { ...cellStyle, borderRight: 'none' };
  const labelStyle = {
    fontSize: 10,
    color: '#666',
    fontWeight: '600',
    marginBottom: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const renderRowComp = (items, isLast) => (
    <Box
      display="flex"
      sx={{
        borderBottom: isLast ? 'none' : '1px solid #e0e0e0',
      }}
    >
      {items.map((item, idx) => (
        <Box key={idx} sx={idx === items.length - 1 ? lastCellStyle : cellStyle}>
          <div style={labelStyle}>{item.label}</div>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: item.color, lineHeight: 1 }}>
            <AnimatedValue targetValue={item.value} />{item.suffix}
          </div>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box display="flex" gap={2} mb={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
      {/* TODAY'S ORDER SUMMARY */}
      <Card
        variant="outlined"
        sx={{ flex: 1, borderRadius: 2, borderColor: '#d0d0d0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <Box sx={{ px: 2, pt: 0.8, pb: 0 }}>
          <Typography
            variant="h1"
            style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 2, marginBottom: 0, display: 'inline-block' }}
          >
            {hubCode} - TODAY'S ORDER SUMMARY
          </Typography>
        </Box>
        {renderRowComp(ROW1, false)}
        {renderRowComp(ROW2, true)}
      </Card>

      {/* THIS YEAR'S ORDER SUMMARY */}
      {/* <Card
        variant="outlined"
        sx={{ flex: 1, borderRadius: 2, borderColor: '#d0d0d0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <Box sx={{ px: 2, pt: 0.8, pb: 0 }}>
          <Typography
            variant="h1"
            style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 2, marginBottom: 0, display: 'inline-block' }}
          >
            {hubCode} -YESTERDAY'S ORDER SUMMARY
          </Typography>
        </Box>
        {renderRowComp(ROW3, true)}
      </Card> */}
      <Card
        variant="outlined"
        sx={{ flex: 1, borderRadius: 2, borderColor: '#d0d0d0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <Box sx={{ px: 2, pt: 0.8, pb: 0 }}>
          <Typography
            variant="h1"
            style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 2, marginBottom: 0, display: 'inline-block' }}
          >
            {hubCode} -TODAY'S RIDER STATUS
          </Typography>
        </Box>
        {renderRowComp(ROW3, false)}
        {renderRowComp(ROW4, true)}
      </Card>
    </Box>
  );
};

const HubOperatorManagerComp = ({
  orderInfo, riderInfo, noticeBoardInfo, onNoticeClick,
  orderWiseWeeklyInfo, orderWiseMonthlyInfo, weeklyChartLabels, monthlyChartLabels,
  riderTotalInfo, merchantInfo, vehicleInfo, hubManagerInfo, hubOperatorInfo, customerInfo,
  LABELS, userTypeId, HUB_MANAGER_ROLE_ID, orderPreferences, hubId,
  emiratesOrderData, timeSlotSummary, hubCode
}) => {
  return (
    <Box sx={{ overflow: 'hidden', padding: { xs: 1, sm: 1, md: 2, lg: 2 }, paddingTop: { xs: 1, sm: 1, md: 1 } }}>
      <OrderCards orderInfo={orderInfo} />
      <Grid mt={0.8} container spacing={1}>
        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CustomCard title="Rider Status" type="Rider" riderInfo={riderInfo} />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CustomCard title="Notice Board" type="Notice" noticeBoardInfo={noticeBoardInfo} onNoticeClick={onNoticeClick} />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <AppWebsiteVisits weeklyChartData={orderWiseWeeklyInfo} monthlyChartData={orderWiseMonthlyInfo} weeklyChartLabels={weeklyChartLabels} monthlyChartLabels={monthlyChartLabels} />
        </Grid>
      </Grid>
      <Grid mt={1} container spacing={2}>
        <Grid item xs={12} md={4}>
          <CustomPaper sx={{ backgroundColor: "#fff" }} variant="outlined">
            <CategoryTitle title="RESOURCE SUMMARY" />
            <CategorySection title="RIDERS" data={riderTotalInfo} labels={LABELS} />
            {orderPreferences?.data?.merchantEnabled ? <CategorySection title="MERCHANTS" data={merchantInfo} labels={LABELS} /> : ""}
            <CategorySection title="VEHICLES" data={vehicleInfo} labels={LABELS} />
            <CategorySection title="HUB MANAGERS" data={hubManagerInfo} labels={LABELS} />
            <CategorySection title="HUB OPERATORS" data={hubOperatorInfo} labels={LABELS} />
            <CategorySection title="HUB CUSTOMERS" data={customerInfo} labels={LABELS} />
          </CustomPaper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <EmiratesOrderTable data={emiratesOrderData} hubName={hubCode} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <TimeSlotOrderSummary data={timeSlotSummary} hubName={hubCode} />
      </Box>

      {/* ── Live MAP + GRID / Assign / Re-Assign / History ── */}
      <LiveMapGrid selectedHubId={hubId} />
    </Box>
  );
};

const ClientAdminComp = ({
  hubWiseDetails, activeIndex, isDynamicEnabled, setIsDynamicEnabled, handleButtonClick,
  hubCode, LABELS, riderTotalInfo, merchantInfo, vehicleInfo, hubManagerInfo, hubOperatorInfo, customerInfo,
  emiratesOrderData, timeSlotSummary, clientSummaryStats, orderPreferences, hubId
}) => {
  return (
    <Box sx={{ overflow: 'hidden', padding: { xs: 1, sm: 1, md: 2, lg: 2 }, paddingTop: { xs: 1, sm: 1, md: 1 } }}>
      <Grid container spacing={2} sx={{ height: 'auto' }}>
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card variant="outlined" sx={{ height: '100%', width: '100%' }}>
            <CardContent sx={{ padding: 2, minHeight: 400, maxHeight: 900, overflowY: 'auto' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography
                  variant="h6"
                  component="div"
                  style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 6 }}
                >
                  HUB SUMMARY
                </Typography>
                <Tooltip title={isDynamicEnabled ? "Auto-cycling ON — click to keep selection static" : "Auto-cycling OFF — click to enable auto-cycle"}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDynamicEnabled}
                        onChange={(e) => setIsDynamicEnabled(e.target.checked)}
                        size="small"
                        color="success"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 11, color: isDynamicEnabled ? '#32CA73' : '#999', fontWeight: 'bold' }}>
                        {isDynamicEnabled ? 'AUTO' : 'STATIC'}
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ ml: 0, mr: 0 }}
                  />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 1 }}>
                {hubWiseDetails.map((item, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    onClick={() => handleButtonClick(item, index)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between', // Align the hub name and code
                      backgroundColor: activeIndex === index ? '#32CA73' : '#fff',
                      color: activeIndex === index ? 'white' : 'black',
                      borderColor: '#32CA73',
                      '&:hover': {
                        backgroundColor: '#32CA73',
                        color: 'white',
                        borderColor: '#32CA73',
                      },
                      fontSize: 12,
                      fontWeight: 'bold',
                      padding: '10px 20px', // Add padding for better spacing
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}.</Typography>
                      <span>{item.hubName}</span>
                    </Box>
                    <span>{item.hubCode}</span>

                  </Button>
                ))}
              </Box>

            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card variant="outlined" sx={{ height: '100%', width: '100%' }}>
            <CardContent sx={{ padding: 2, minHeight: 400, maxHeight: 900, overflowY: 'auto' }}>
              <Typography
                variant="h6"
                component="div"
                style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 6, marginBottom: 8 }}
              >
                <HeadingHubName hubCode={hubCode} /> - RESOURCE SUMMARY
              </Typography>
              <>
                <HeaderRow labels={LABELS} />
                <InfoRow title="RIDERS" infoList={riderTotalInfo} />
                {orderPreferences?.data?.merchantEnabled ? <InfoRow title="MERCHANTS" infoList={merchantInfo} /> : ""}
                <InfoRow title="VEHICLES" infoList={vehicleInfo} />
                <Typography
                  variant="h6"
                  component="div"
                  style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 6, marginBottom: 8, marginTop: 16 }}
                >
                  MANAGEMENT
                </Typography>
                <InfoRow title="HUB MANAGERS" infoList={hubManagerInfo} />
                <InfoRow title="HUB OPERATORS" infoList={hubOperatorInfo} />
                <InfoRow title="HUB CUSTOMERS" infoList={customerInfo} />
              </>
            </CardContent>
          </Card>
        </Grid>

        {/* Emirates Order Table - same level as HUB SUMMARY & HUB DASHBOARD */}
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <EmiratesOrderTable data={emiratesOrderData} hubName={hubCode} />
        </Grid>

      </Grid>

      {/* ── Today's Order Summary ── */}
      <Box sx={{ mt: 2 }}>
        <ClientSummaryBarComp clientSummaryStats={clientSummaryStats} hubCode={hubCode} />
      </Box>

      {/* ── Time Slot wise Order Summary ── */}
      <TimeSlotOrderSummary data={timeSlotSummary} hubName={hubCode} />

      {/* ── Live MAP + GRID / Assign / Re-Assign / History ── */}
      <LiveMapGrid selectedHubId={hubId} />
    </Box>
  );
};

const MerchantComp = ({
  orderWiseWeeklyInfo, orderWiseMonthlyInfo, weeklyChartLabels, monthlyChartLabels,
  orderInfo, merchantItemIcon, customerInfo, noticeBoardInfo, onNoticeClick
}) => {
  return (
    <Grid container spacing={2}>
      <Grid mt={1} item xs={12} md={9} lg={9.4}>
        <MerchantNoticeBoard title="Notice Board" noticeBoardInfo={noticeBoardInfo} onNoticeClick={onNoticeClick} />
        <AppWebsiteVisits weeklyChartData={orderWiseWeeklyInfo} monthlyChartData={orderWiseMonthlyInfo} weeklyChartLabels={weeklyChartLabels} monthlyChartLabels={monthlyChartLabels} merchant={true} />
      </Grid>
      <Grid mt={1} sx={{ height: 'auto' }} item xs={12} md={3} lg={2.5}>
        <CustomPaper sx={{ backgroundColor: "#fff" }} variant="outlined">
          <CategoryTitle title={"ORDER SUMMARY"} />
          <Box>
            {
              orderInfo.map((item, index) => (
                <MerchantItem key={index} icon={item.icon} title={item.title} value={item.value} index={index} />
              ))}
          </Box>
          <CategoryTitle title={"CUSTOMERS"} />
          <Box >
            {customerInfo.map((item, index) => (
              <ListItem key={index} icon={item.icon} title={item.title} value={item.value} index={index} />
            ))}
          </Box>
        </CustomPaper>
      </Grid>
    </Grid>
  );
};

const AdminComp = ({ noticeBoardInfo = [], adminInfo = [], selectedHubId }) => (
  <Grid container spacing={2}>
    <Grid mt={1} item xs={12} md={6}>
      <CustomPaper sx={{ backgroundColor: "#fff" }} variant="outlined">
        <Typography sx={{ fontSize: 30 }} variant='h1'>Client Notice Board</Typography>

        {
          (noticeBoardInfo || []).map((item, index) => (
            <React.Fragment key={index}>
              <ListItemButton onClick={() => { }}>
                <Box display="flex" alignItems="center" width="100%">
                  <ListItemText primary={item.title?.toUpperCase()} sx={{ flex: 1, textAlign: "left", fontSize: 18 }} />
                  <Typography variant="h6" sx={{ ml: 2, fontSize: 25 }}>{item.value}</Typography>
                </Box>
              </ListItemButton>
              {index < (noticeBoardInfo || []).length - 1 && <Divider />}
            </React.Fragment>
          ))
        }
      </CustomPaper>
    </Grid>

    <Grid mt={1} item xs={12} md={6}>
      <CustomPaper sx={{ backgroundColor: "#fff" }} variant="outlined">
        <Typography sx={{ fontSize: 30 }} variant='h1'>Client Summary</Typography>
        {
          (adminInfo || []).map((item, index) => (
            <ListItem key={index} icon={item.icon} title={item.title}
              value={item.value} index={index} />

          ))}

      </CustomPaper>
    </Grid>
  </Grid>
);

const POGDashBoard = () => {
  const orderPreferences = useSelector(state => state.orderPreferences);
  const LABELS = ['TOTAL', 'ACTIVE', 'INACTIVE'];
  const [redirectPath, setRedirectPath] = useState(null);

  const theme = createTheme({
    typography: {
      fontFamily: 'ProductSansLight, sans-serif', // Use the custom font family
      h1: {
        fontFamily: 'ProductSansBold, sans-serif',
      },
      h2: {
        fontFamily: 'ProductSansBold, sans-serif',
      },
      h3: {
        fontFamily: 'ProductSansBold, sans-serif',

      },
      h6: {
        fontFamily: 'ProductSansBold, sans-serif',
      },
      subtitle1: {
        fontFamily: 'ProductSansLight, sans-serif',
        fontSize: 20
      },

      body1: 'ProductSansLight, sans-serif',
    },

  });

  const defaultVehicleInfo = [
    { title: "Total Vehicles", icon: TotalIcon, value: 0 },
    { title: "Active Vehicles", icon: ActiveIcon, value: 0 },
    { title: "Inactive Vehicles", icon: InActiveIcon, value: 0 },
  ];

  const defaultMerchantInfo = [
    { title: "Total Merchants", icon: TotalIcon, value: 0 },
    { title: "Active Merchants", icon: ActiveIcon, value: 0 },
    { title: "Inactive Merchants", icon: InActiveIcon, value: 0 },
  ];

  const defaultHubOperatorInfo = [
    { title: "Total Hub Operators", icon: TotalIcon, value: 0 },
    { title: "Active Hub Operators", icon: ActiveIcon, value: 0 },
    { title: "Inactive Hub Operators", icon: InActiveIcon, value: 0 },
  ];

  const defaultCustomerInfo = [
    { title: "Total Customers", icon: TotalIcon, value: 0 },
    { title: "Active Customers", icon: ActiveIcon, value: 0 },
    { title: "Inactive Customers", icon: InActiveIcon, value: 0 },
  ];

  const defaultHubManagerInfo = [
    { title: "Total Managers", icon: TotalIcon, value: 0 },
    { title: "Active Managers", icon: ActiveIcon, value: 0 },
    { title: "Inactive Managers", icon: InActiveIcon, value: 0 },
  ];

  const defaultRiderTotalInfo = [
    { title: "Total Riders", icon: TotalIcon, value: 0 },
    { title: "Active Riders", icon: ActiveIcon, value: 0 },
    { title: "Inactive Riders", icon: InActiveIcon, value: 0 },
  ];

  const [userTypeId, setuserTypeId] = useState(0);
  const [vehicleInfo, setvehicleInfo] = useState(defaultVehicleInfo);
  const [merchantInfo, setmerchantInfo] = useState(defaultMerchantInfo);
  const [hubOperatorInfo, sethubOperatorInfo] = useState(defaultHubOperatorInfo);
  const [customerInfo, setcustomerInfo] = useState(defaultCustomerInfo);
  const [hubManagerInfo, sethubManagerInfo] = useState(defaultHubManagerInfo);
  const [riderTotalInfo, setriderTotalInfo] = useState(defaultRiderTotalInfo);
  const [riderInfo, setriderInfo] = useState(null);
  const [orderInfo, setorderInfo] = useState([]);
  const [orderWiseMonthlyInfo, setorderWiseMonthlyInfo] = useState([]);
  const [orderWiseWeeklyInfo, setorderWiseWeeklyInfo] = useState([]);
  const [monthlyChartLabels, setMonthlyChartLabels] = useState([]);
  const [weeklyChartLabels, setWeeklyChartLabels] = useState([]);
  const [noticeBoardInfo, setnoticeBoardInfo] = useState([]);
  // const [clientAdminInfo, setClientAdminInfo] = useState([]);
  const [hubWiseDetails, sethubWiseDetails] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  // const [totalOrders, setTotalOrders] = useState(0);
  const [adminInfo, setAdminInfo] = useState([]);
  const [hubCode, setHubCode] = useState(null);
  const [hubId, setHubId] = useState(null);
  const [isDynamicEnabled, setIsDynamicEnabled] = useState(true);
  const [emiratesOrderData, setEmiratesOrderData] = useState([]);
  const [timeSlotSummary, setTimeSlotSummary] = useState([]);
  const [clientSummaryStats, setClientSummaryStats] = useState({
    totalOrdersToday: 0,
    otdToday: 0,
    availableDrivers: 0,
    vansFiling: 0,
    ordersNotAssigned: 0,
    ordersOnRoad: 0,
    ordersDelivered: 0,
    ordersCancelled: 0,
    todayUndeliveredOrders: 0,
    chatReceived: 0,
    // New Rider Availability Stats
    onDuty: 0,
    availableRiders: 0,
    offDuty: 0,
    onVacation: 0,
    onBreak: 0,
    deliveryBusy: 0,
    busyOnHub: 0,
    freeAtHub: 0,
    deliveredFreed: 0,
    unavailable: 0
  });

  const handleClick = (item) => {
    if (item.title === "Assign") {
      setRedirectPath("/web_app/pnd_order/order_details");
    }
    else if (item.title === "Reschedule") {
      setRedirectPath("/web_app/pnd_order/order-reschedule");
    }
    else if (item.title === "Cancel") {
      setRedirectPath("/web_app/pnd_order_cancel/order_details");
    }
  };

  useEffect(() => {
    getDashBoard();
    const refreshInterval = setInterval(getDashBoard, DASHBOARD_REFRESH_INTERVAL);
    return () => clearInterval(refreshInterval);
  }, [])


  const getDashBoard = async () => {
    const userTypeStr = localStorage.getItem("uri");
    const userTypeNumberId = userTypeStr ? Number(userTypeStr) : 0;
    setuserTypeId(userTypeNumberId);

    if (userTypeNumberId == HUB_MANAGER_ROLE_ID || userTypeNumberId == HUB_OPERATOR_ROLE_ID) {

      const requestData = {
        hubId: localStorage.getItem("hi"),
        userTypeId: localStorage.getItem("uri"),
        customerId: localStorage.getItem("ci"),
      };

      try {
        const res = await PndRestApi.GetDashBoardData(requestData);
        if (res.data.status === 200 && res.data.data) {
          const hubUsersInfo = res.data.data.hubUsersInfo || {};

          setvehicleInfo([
            { title: "Total Vehicles", icon: TotalIcon, value: hubUsersInfo.generalInfo?.vehicleInfo?.total || 0 },
            { title: "Active Vehicles", icon: ActiveIcon, value: hubUsersInfo.generalInfo?.vehicleInfo?.active || 0 },
            { title: "Inactive Vehicles", icon: InActiveIcon, value: hubUsersInfo.generalInfo?.vehicleInfo?.inactive || 0 },
          ]);

          setmerchantInfo([
            { title: "Total Merchants", icon: TotalIcon, value: hubUsersInfo.generalInfo?.merchantInfo?.total || 0 },
            { title: "Active Merchants", icon: ActiveIcon, value: hubUsersInfo.generalInfo?.merchantInfo?.active || 0 },
            { title: "Inactive Merchants", icon: InActiveIcon, value: hubUsersInfo.generalInfo?.merchantInfo?.inactive || 0 },
          ]);

          sethubOperatorInfo([
            { title: "Total Hub Operators", icon: TotalIcon, value: hubUsersInfo.generalInfo?.hubOperatorInfo?.total || 0 },
            { title: "Active Hub Operators", icon: ActiveIcon, value: hubUsersInfo.generalInfo?.hubOperatorInfo?.active || 0 },
            { title: "Inactive Hub Operators", icon: InActiveIcon, value: hubUsersInfo.generalInfo?.hubOperatorInfo?.inactive || 0 },
          ]);

          setcustomerInfo([
            { title: "Total Customers", icon: TotalIcon, value: hubUsersInfo.generalInfo?.customerInfo?.total || 0 },
            { title: "Active Customers", icon: ActiveIcon, value: hubUsersInfo.generalInfo?.customerInfo?.active || 0 },
            { title: "Inactive Customers", icon: InActiveIcon, value: hubUsersInfo.generalInfo?.customerInfo?.inactive || 0 },
          ]);

          const activeCount = hubUsersInfo.generalInfo?.riderInfo?.active || 0;
          const inactiveCount = hubUsersInfo.generalInfo?.riderInfo?.inactive || 0;

          setriderTotalInfo([
            { title: "Total Riders", icon: TotalIcon, value: activeCount + inactiveCount || 0 },
            { title: "Active Riders", icon: ActiveIcon, value: activeCount || 0 },
            { title: "Inactive Riders", icon: InActiveIcon, value: inactiveCount || 0 },
          ]);
          setriderInfo(hubUsersInfo.generalInfo?.riderInfo);

          setorderInfo([
            { title: "Total Punched", icon: totalPunchedIcon, value: hubUsersInfo.orderInfo?.orderInfo?.totalPunchedOrder || 0 },
            { title: "Pick&Drop", icon: PicknDropIcon, value: hubUsersInfo.orderInfo?.orderInfo?.totalPickAndDropOrder || 0 },
            { title: "PickUp", icon: pickUpIcon, value: hubUsersInfo.orderInfo?.orderInfo?.totalPickUpOrder || 0 },
            { title: "DropOff", icon: dropOffIcon, value: hubUsersInfo.orderInfo?.orderInfo?.totalDropOffOrder || 0 },
            { title: "Cancelled", icon: totalCancelledIcon, value: hubUsersInfo.orderInfo?.orderInfo?.cancelled || 0, },
            { title: "Rescheduled", icon: totalRescheduledIcon, value: hubUsersInfo.orderInfo?.orderInfo?.rescheduled || 0 },
            { title: "Returned", icon: totalReturnedIcon, value: hubUsersInfo.orderInfo?.orderInfo?.returned || 0 },
            { title: "Full Delivered", icon: totalDeliveredIcon, value: hubUsersInfo.orderInfo?.orderInfo?.fullDelivered || 0 },
            { title: "Partial Delivered", icon: totalPartialIcon, value: hubUsersInfo.orderInfo?.orderInfo?.partialDelivered || 0 },
            { title: "Undelivered", icon: totalUndeliveredIcon, value: hubUsersInfo.orderInfo?.orderInfo?.undelivered || 0 },
          ]);


          const extractedMonthlyLabels = hubUsersInfo.orderWiseMonthlyInfo.map(month => month.name);
          const extractedWeeklyLabels = hubUsersInfo.orderWiseWeeklyInfo.map(day => day.name);

          //console.log('extractedWeeklyLabels',extractedWeeklyLabels)

          setMonthlyChartLabels(extractedMonthlyLabels);
          setWeeklyChartLabels(extractedWeeklyLabels);

          const totalOrdersWeekly = hubUsersInfo.orderInfo?.orderWeeklyTotalInfo?.total || 0;
          const totalOrdersYearly = hubUsersInfo.orderInfo?.orderYearlyTotalInfo?.total || 0;
          const PickUpWeekly = hubUsersInfo.orderInfo?.orderWeeklyTotalInfo?.pickUp || 0;
          const PickUpYearly = hubUsersInfo.orderInfo?.orderYearlyTotalInfo?.pickUp || 0;
          const DropOffWeekly = hubUsersInfo.orderInfo?.orderWeeklyTotalInfo?.dropOff || 0;
          const DropOffYearly = hubUsersInfo.orderInfo?.orderYearlyTotalInfo?.dropOff || 0;
          const PickAndDropWeekly = hubUsersInfo.orderInfo?.orderWeeklyTotalInfo?.pickAndDrop || 0;
          const PickAndDropYearly = hubUsersInfo.orderInfo?.orderYearlyTotalInfo?.pickAndDrop || 0;


          const weeklyData = (hubUsersInfo.orderWiseWeeklyInfo || []);
          const weeklyChartData = mapWeeklyData(weeklyData, totalOrdersWeekly, PickUpWeekly, DropOffWeekly, PickAndDropWeekly);
          setorderWiseWeeklyInfo(weeklyChartData);
          const monthlyData = (hubUsersInfo.orderWiseMonthlyInfo || []);
          const monthlyChartData = mapMonthlyData(monthlyData, totalOrdersYearly, PickUpYearly, DropOffYearly, PickAndDropYearly);
          setorderWiseMonthlyInfo(monthlyChartData);
          setnoticeBoardInfo(
            (hubUsersInfo.noticeBoardInfo || [])
              .filter(item => {

                // Remove "Cancel" for HUB_OPERATOR_ROLE_ID
                return !(userTypeNumberId == HUB_OPERATOR_ROLE_ID && item.label === "Cancel");
              })
              .map(item => {
                return {
                  title: item.label || "",
                  value: item.count || 0,
                  icon: getIcon(item.label),
                };
              })
          );
          setEmiratesOrderData(hubUsersInfo.emiratesWiseOrderInfo || []);
          setTimeSlotSummary(hubUsersInfo.timeSlotWiseOrderSummary || []);
          if (hubUsersInfo.hubName) {
            let hc = hubUsersInfo.hubName + " (" + (hubUsersInfo.hubCode || "") + ")";
            setHubCode(hc.toUpperCase());
          }
          setHubId(localStorage.getItem("hi"));
        }
        else {
        }

      } catch (error) {
      }
    }

    else if (userTypeNumberId == CLIENT_ADMIN) {
      const requestData = {
        userTypeId: localStorage.getItem("uri"),
        customerId: localStorage.getItem("ci"),
      };
      try {
        const res = await PndRestApi.GetDashBoardData(requestData);
        if (res.data.status === 200 && res.data.data) {
          const clientAdminInfo = res.data.data.clientAdminInfo || {};
          // setClientAdminInfo([
          //   { title: "Hub", icon: TotalIcon, value: clientAdminInfo?.totalHubs || 0 },
          //   // { title: "Active Hub", icon: TotalIcon, value: clientAdminInfo?.totalActiveHub || 0 },
          //   { title: "Vehicles", icon: TotalIcon, value: clientAdminInfo?.totalVehicles || 0 },
          //   { title: "Riders", icon: TotalIcon, value: clientAdminInfo?.totalRiders || 0 },
          //   { title: "Hub Managers", icon: TotalIcon, value: clientAdminInfo?.totalHubManagers || 0 },
          //   { title: "Hub Operators", icon: TotalIcon, value: clientAdminInfo?.totalHubOperators || 0 },
          //   ...(orderPreferences?.data?.merchantEnabled
          //     ? [{ title: "Merchants", icon: TotalIcon, value: clientAdminInfo?.totalMerchants || 0 }]
          //     : []),
          //   { title: "Leased Rider Agencies", icon: TotalIcon, value: clientAdminInfo?.totalDriverEmployers || 0 },
          //   { title: "Customers", icon: TotalIcon, value: clientAdminInfo?.totalCustomer || 0 }
          // ])
          sethubWiseDetails(clientAdminInfo.hubWiseDetails || []);
          setClientSummaryStats({
            totalOrdersToday: clientAdminInfo?.todayTotalOrders || 0,
            otdToday: clientAdminInfo?.todayOtdInPercentage || 0,
            availableDrivers: clientAdminInfo?.availableDrivers || 0,
            vansFiling: clientAdminInfo?.vanFillingPercentage || 0,
            ordersNotAssigned: clientAdminInfo?.todayOrdersNotAssigned || 0,
            ordersOnRoad: clientAdminInfo?.todayOrdersOnTheRoad || 0,
            ordersDelivered: clientAdminInfo?.todayOrdersDelivered || 0,
            ordersCancelled: clientAdminInfo?.todayOrdersCancelled || 0,
            todayUndeliveredOrders: clientAdminInfo?.todayUndeliveredOrders || 0,
            chatReceived: clientAdminInfo?.chatReceived || 0,
            onDuty: clientAdminInfo?.riderAvailabilityInfo?.onDuty || 0,
            availableRiders: clientAdminInfo?.riderAvailabilityInfo?.availableRiders || 0,
            offDuty: clientAdminInfo?.riderAvailabilityInfo?.offDuty || 0,
            onVacation: clientAdminInfo?.riderAvailabilityInfo?.onVacation || 0,
            onBreak: clientAdminInfo?.riderAvailabilityInfo?.onBreak || 0,
            deliveryBusy: clientAdminInfo?.riderAvailabilityInfo?.onDutyInfo?.deliveryBusy || 0,
            busyOnHub: clientAdminInfo?.riderAvailabilityInfo?.onDutyInfo?.busyOnHub || 0,
            freeAtHub: clientAdminInfo?.riderAvailabilityInfo?.onDutyInfo?.freeAtHub || 0,
            deliveredFreed: clientAdminInfo?.riderAvailabilityInfo?.onDutyInfo?.deliveredFreed || 0,
            unavailable: clientAdminInfo?.riderAvailabilityInfo?.onDutyInfo?.unavailable || 0,
          });
        }
        else {
        }
      } catch (error) {
      }
    }

    else if (userTypeNumberId == MERCHANT_ROLE_ID) {
      const requestData = {
        hubId: localStorage.getItem("hi"),
        userTypeId: localStorage.getItem("uri"),
        customerId: localStorage.getItem("ci"),
        merchantId: localStorage.getItem("mi"),
      };

      try {
        const res = await PndRestApi.GetDashBoardData(requestData);
        if (res.data.status === 200 && res.data.data) {
          const merchantData = res.data.data;
          const PickUpWeekly = merchantData.orderInfo?.orderWeeklyTotalInfo?.pickUp || 0;
          const PickUpYearly = merchantData.orderInfo?.orderYearlyTotalInfo?.pickUp || 0;
          // Extract order information
          const orderWiseWeeklyInfo = merchantData.orderWiseWeeklyInfo || [];
          const orderWiseMonthlyInfo = merchantData.orderWiseMonthlyInfo || [];

          // Weekly Chart Data (Pickup Only)
          const weeklyData = orderWiseWeeklyInfo.filter(day => day.pickup);
          const monthlyData = orderWiseMonthlyInfo.filter(month => month.pickup);

          // Monthly Chart Data (Pickup Only)
          const weeklyLabels = (orderWiseMonthlyInfo.filter(day => day.pickup)).map(day => day.name);
          const monthlyLabels = (orderWiseMonthlyInfo.filter(month => month.pickup)).map(month => month.name); // Months with pickup data
          const weeklyChartData = mapMerchantWeeklyData(weeklyData, PickUpWeekly);
          const monthlyChartData = mapMerchantMonthlyData(monthlyData, PickUpYearly);

          // Set state for both weekly and monthly chart data
          setWeeklyChartLabels(weeklyLabels);
          setorderWiseWeeklyInfo(weeklyChartData);
          setMonthlyChartLabels(monthlyLabels);
          setorderWiseMonthlyInfo(monthlyChartData);

          // Set state with the extracted values
          setorderInfo([
            { title: "PickUp", icon: pickUpIcon, value: merchantData.orderInfo?.orderInfo?.totalPickUpOrder || 0 },
            { title: "Cancelled", icon: totalCancelledIcon, value: merchantData.orderInfo?.orderInfo?.cancelled || 0 },
            { title: "Rescheduled", icon: totalRescheduledIcon, value: merchantData.orderInfo?.orderInfo?.rescheduled || 0 },
            { title: "Returned", icon: totalReturnedIcon, value: merchantData.orderInfo?.orderInfo?.returned || 0 },
            { title: "Full Delivered", icon: totalDeliveredIcon, value: merchantData.orderInfo?.orderInfo?.fullDelivered || 0 },
            { title: "Partial Delivered", icon: totalPartialIcon, value: merchantData.orderInfo?.orderInfo?.partialDelivered || 0 },
            { title: "Undelivered", icon: totalUndeliveredIcon, value: merchantData.orderInfo?.orderInfo?.undelivered || 0 },
            { title: "Withdrawn", icon: WithdrawnIcon, value: merchantData.orderInfo?.orderInfo?.withdrawn || 0 },
          ]);

          // Set notice board info with icon mapping
          setnoticeBoardInfo((merchantData.noticeBoardInfo || []).map(item => {
            let icon = null;
            switch (item.label) {
              case "Pick Up Ready":
                icon = AssignNoticeIcon;
                break;
              default:
                break;
            }
            return {
              title: item.label || "",
              value: item.count || 0,
              icon: icon
            };
          }));

          // Set customer info
          setcustomerInfo([
            { title: "Total Customers", icon: TotalIcon, value: merchantData?.customerInfo?.total || 0 },
            { title: "Active Customers", icon: ActiveIcon, value: merchantData?.customerInfo?.active || 0 },
            { title: "Inactive Customers", icon: InActiveIcon, value: merchantData?.customerInfo?.inactive || 0 },
          ]);
        } else {
          // Handle API error or empty response
          console.error("Error: Invalid response status or empty data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }

    }
    else if (userTypeNumberId == ITC_ADMIN_ROLE_ID) {
      const requestData = {
        userTypeId: localStorage.getItem("uri"),
        customerId: localStorage.getItem("ci"),
      };
      try {
        const res = await PndRestApi.GetDashBoardData(requestData);
        if (res.data.status === 200 && res.data.data) {
          const adminInfoData = res.data.data.itcAdminInfo.clientInfo;
          const noticeInfo = res.data.data.itcAdminInfo.noticeInfo;
          console.log('adminInfoData', adminInfoData)
          console.log('noticeInfo', noticeInfo)
          setAdminInfo([
            { title: "Total Clients", icon: TotalIcon, value: adminInfoData?.total || 0 },
            { title: "Active Clients", icon: ActiveIcon, value: adminInfoData?.active || 0 },
            { title: "Inactive Clients", icon: InActiveIcon, value: adminInfoData?.inactive || 0 },
          ]);
          setnoticeBoardInfo((noticeInfo || []).map(item => {
            return {
              title: item.label || "",
              value: item.expiryDate || 0,
            };
          }));

        }
      } catch (error) {
      }
    }


  };






  // ─────────────────────────────────────────────────────────────────────────────

  const handleButtonClick = (item, index) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    if (userTypeId !== CLIENT_ADMIN || !hubWiseDetails || hubWiseDetails.length === 0) return;

    if (activeIndex === null) {
      setActiveIndex(0);
      return;
    }

    const item = hubWiseDetails[activeIndex];
    if (item) {
      let hc = item?.hubName + " (" + item?.hubCode + ")";
      setHubCode(hc ? hc.toUpperCase() : "");
      setHubId(item?.hubId);
      setvehicleInfo([
        { title: "Total Vehicles", icon: TotalIcon, value: item?.vehicleInfo?.total || 0 },
        { title: "Active Vehicles", icon: ActiveIcon, value: item?.vehicleInfo?.active || 0 },
        { title: "Inactive Vehicles", icon: InActiveIcon, value: item?.vehicleInfo?.inactive || 0 },
      ]);
      setmerchantInfo([
        { title: "Total Merchants", icon: TotalIcon, value: item?.merchantInfo?.total || 0 },
        { title: "Active Merchants", icon: ActiveIcon, value: item?.merchantInfo?.active || 0 },
        { title: "Inactive Merchants", icon: InActiveIcon, value: item?.merchantInfo?.inactive || 0 },
      ]);
      sethubOperatorInfo([
        { title: "Total Hub Operators", icon: TotalIcon, value: item?.hubOperatorInfo?.total || 0 },
        { title: "Active Hub Operators", icon: ActiveIcon, value: item?.hubOperatorInfo?.active || 0 },
        { title: "Inactive Hub Operators", icon: InActiveIcon, value: item?.hubOperatorInfo?.inactive || 0 },
      ]);
      setcustomerInfo([
        { title: "Total Customers", icon: TotalIcon, value: item?.customerInfo?.total || 0 },
        { title: "Active Customers", icon: ActiveIcon, value: item?.customerInfo?.active || 0 },
        { title: "Inactive Customers", icon: InActiveIcon, value: item?.customerInfo?.inactive || 0 },
      ]);
      sethubManagerInfo([
        { title: "Total Managers", icon: TotalIcon, value: item?.hubManagerInfo?.total || 0 },
        { title: "Active Managers", icon: ActiveIcon, value: item?.hubManagerInfo?.active || 0 },
        { title: "Inactive Managers", icon: InActiveIcon, value: item?.hubManagerInfo?.inactive || 0 },
      ]);
      setriderTotalInfo([
        { title: "Total Riders", icon: TotalIcon, value: item?.riderInfo?.total || 0 },
        { title: "Active Riders", icon: ActiveIcon, value: item?.riderInfo?.active || 0 },
        { title: "Inactive Riders", icon: InActiveIcon, value: item?.riderInfo?.inactive || 0 },
      ]);
      setClientSummaryStats({
        totalOrdersToday: item?.todayTotalOrders || 0,
        otdToday: item?.todayOtdInPercentage || 0,
        availableDrivers: item?.availableDrivers || 0,
        vansFiling: item?.vanFillingPercentage || 0,
        ordersNotAssigned: item?.todayOrdersNotAssigned || 0,
        ordersOnRoad: item?.todayOrdersOnTheRoad || 0,
        ordersDelivered: item?.todayOrdersDelivered || 0,
        ordersCancelled: item?.todayOrdersCancelled || 0,
        todayUndeliveredOrders: item?.todayUndeliveredOrders || 0,
        chatReceived: item?.chatReceived || 0,
        onDuty: item?.riderAvailabilityInfo?.onDuty || 0,
        availableRiders: item?.riderAvailabilityInfo?.availableRiders || 0,
        offDuty: item?.riderAvailabilityInfo?.offDuty || 0,
        onVacation: item?.riderAvailabilityInfo?.onVacation || 0,
        onBreak: item?.riderAvailabilityInfo?.onBreak || 0,
        deliveryBusy: item?.riderAvailabilityInfo?.onDutyInfo?.deliveryBusy || 0,
        busyOnHub: item?.riderAvailabilityInfo?.onDutyInfo?.busyOnHub || 0,
        freeAtHub: item?.riderAvailabilityInfo?.onDutyInfo?.freeAtHub || 0,
        deliveredFreed: item?.riderAvailabilityInfo?.onDutyInfo?.deliveredFreed || 0,
        unavailable: item?.riderAvailabilityInfo?.onDutyInfo?.unavailable || 0,
      });
      setEmiratesOrderData(item?.emiratesWiseOrderInfo || []);
      setTimeSlotSummary(item?.timeSlotWiseOrderSummary || []);
    }
  }, [hubWiseDetails, activeIndex, userTypeId]);

  useEffect(() => {
    if (userTypeId !== CLIENT_ADMIN || !isDynamicEnabled || !hubWiseDetails || hubWiseDetails.length === 0) return;

    const intervalId = setInterval(() => {
      const safeIndex = (activeIndex === null || activeIndex === undefined) ? -1 : activeIndex;
      const nextIndex = safeIndex >= hubWiseDetails.length - 1 ? 0 : safeIndex + 1;

      const nextItem = hubWiseDetails[nextIndex];
      handleButtonClick(nextItem, nextIndex);
    }, 10000); // 10 seconds for visual stability, instead of manic 2s!

    return () => clearInterval(intervalId);
  }, [hubWiseDetails, isDynamicEnabled, userTypeId, activeIndex]);




  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  const renderDashboardComponent = () => {
    switch (userTypeId) {
      case HUB_MANAGER_ROLE_ID:
      case HUB_OPERATOR_ROLE_ID:
        return (
          <HubOperatorManagerComp
            orderInfo={orderInfo}
            riderInfo={riderInfo}
            noticeBoardInfo={noticeBoardInfo}
            onNoticeClick={handleClick}
            orderWiseWeeklyInfo={orderWiseWeeklyInfo}
            orderWiseMonthlyInfo={orderWiseMonthlyInfo}
            weeklyChartLabels={weeklyChartLabels}
            monthlyChartLabels={monthlyChartLabels}
            riderTotalInfo={riderTotalInfo}
            merchantInfo={merchantInfo}
            vehicleInfo={vehicleInfo}
            hubManagerInfo={hubManagerInfo}
            hubOperatorInfo={hubOperatorInfo}
            customerInfo={customerInfo}
            LABELS={LABELS}
            userTypeId={userTypeId}
            HUB_MANAGER_ROLE_ID={HUB_MANAGER_ROLE_ID}
            orderPreferences={orderPreferences}
            hubId={hubId}
            emiratesOrderData={emiratesOrderData}
            timeSlotSummary={timeSlotSummary}
            hubCode={hubCode}
          />
        );
      case SECURITY_ROLE_ID:
        return <SecurityApproval />;
      case CLIENT_ADMIN:
        return (
          <ClientAdminComp
            hubWiseDetails={hubWiseDetails}
            activeIndex={activeIndex}
            isDynamicEnabled={isDynamicEnabled}
            setIsDynamicEnabled={setIsDynamicEnabled}
            handleButtonClick={handleButtonClick}
            hubCode={hubCode}
            LABELS={LABELS}
            riderTotalInfo={riderTotalInfo}
            merchantInfo={merchantInfo}
            vehicleInfo={vehicleInfo}
            hubManagerInfo={hubManagerInfo}
            hubOperatorInfo={hubOperatorInfo}
            customerInfo={customerInfo}
            emiratesOrderData={emiratesOrderData}
            timeSlotSummary={timeSlotSummary}
            clientSummaryStats={clientSummaryStats}
            orderPreferences={orderPreferences}
            hubId={hubId}
          />
        );
      case MERCHANT_ROLE_ID:
        return (
          <MerchantComp
            orderWiseWeeklyInfo={orderWiseWeeklyInfo}
            orderWiseMonthlyInfo={orderWiseMonthlyInfo}
            weeklyChartLabels={weeklyChartLabels}
            monthlyChartLabels={monthlyChartLabels}
            orderInfo={orderInfo}
            customerInfo={customerInfo}
            noticeBoardInfo={noticeBoardInfo}
            onNoticeClick={handleClick}
          />
        );
      default:
        return (
          <AdminComp
            noticeBoardInfo={noticeBoardInfo}
            adminInfo={adminInfo}
            selectedHubId={hubId}
          />
        );
    }
  };

  const DashboardComponent = renderDashboardComponent();

  return (
    <ThemeProvider theme={theme}>
      {DashboardComponent}
    </ThemeProvider>
  );

};

export default POGDashBoard;
